import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { resolveSeo } from '@/seo/seoData';
import { SITE_NAME } from '@/seo/site';

function ensureMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function removeMeta(attr: 'name' | 'property', key: string) {
  const el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (el) {
    el.remove();
  }
}

function ensureLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function removeLink(rel: string) {
  const el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (el) {
    el.remove();
  }
}

export default function SeoManager() {
  const { pathname } = useLocation();

  useEffect(() => {
    const seo = resolveSeo(pathname);

    document.title = seo.title;
    ensureMeta('name', 'description', seo.description);
    ensureMeta('name', 'keywords', seo.keywords);

    if (seo.robots) {
      ensureMeta('name', 'robots', seo.robots);
    } else {
      removeMeta('name', 'robots');
    }

    if (seo.canonical) {
      ensureLink('canonical', seo.canonical);
    } else {
      removeLink('canonical');
    }

    ensureMeta('property', 'og:title', seo.title);
    ensureMeta('property', 'og:description', seo.description);
    ensureMeta('property', 'og:type', seo.ogType);
    ensureMeta('property', 'og:url', seo.canonical);
    ensureMeta('property', 'og:site_name', SITE_NAME);
    ensureMeta('property', 'og:locale', 'pt_BR');

    if (seo.ogImage) {
      ensureMeta('property', 'og:image', seo.ogImage);
    } else {
      removeMeta('property', 'og:image');
    }

    ensureMeta('name', 'twitter:card', 'summary_large_image');
    ensureMeta('name', 'twitter:title', seo.title);
    ensureMeta('name', 'twitter:description', seo.description);

    if (seo.ogImage) {
      ensureMeta('name', 'twitter:image', seo.ogImage);
    } else {
      removeMeta('name', 'twitter:image');
    }

    ensureMeta('name', 'last-modified', seo.lastModified);
  }, [pathname]);

  return null;
}